import React, { useEffect, useRef, useState } from "react";

import {
  REPLACE_DRAFT,
  draftNotReady,
  pendingDraftNote,
  replaceDraftWarning,
} from "./flowCopy";
import type { PendingDraft } from "../../Types/AccountPlan";

interface Props {
  pendingDraft: PendingDraft;
  /** The file the AE picked after confirming the replacement. */
  onReplace: (file: File) => void;
  /** File types the picker offers (".pptx,.pdf"). */
  accept: string;
  /** Open the confirm straight away (the panel's "Replace this draft" link). */
  startConfirming?: boolean;
  /** The server's own refusal, from a tab that still offered the drop zone. */
  serverMessage?: string | null;
  disabled?: boolean;
}

const BTN =
  "inline-flex h-8 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";
const DANGER =
  "inline-flex h-8 items-center whitespace-nowrap rounded-lg bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 disabled:opacity-50";

/**
 * In place of the drop zone while a confirmed draft isn't published (rule,
 * Sushant 2026-10-30): what blocks the upload, and one way past it, "Replace
 * this draft", which first names what is lost and only then opens the picker.
 */
const PendingDraftNote: React.FC<Props> = ({
  pendingDraft,
  onReplace,
  accept,
  startConfirming = false,
  serverMessage,
  disabled = false,
}) => {
  const [confirming, setConfirming] = useState(startConfirming);
  // A briefing still being written (or stalled) isn't a draft in review: a plain
  // note, not the amber "publish it" box (UI tour A item 1).
  const notReady = draftNotReady(pendingDraft);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (startConfirming) setConfirming(true);
  }, [startConfirming]);

  return (
    <div
      data-testid="pending-draft"
      data-state={pendingDraft.state}
      className={`rounded-lg border px-4 py-3 text-sm text-gray-800 ${
        notReady
          ? "border-gray-200 bg-gray-50"
          : "border-amber-200 bg-amber-50/60"
      }`}
    >
      {serverMessage && (
        <p role="alert" className="mb-1 font-medium text-amber-900">
          {serverMessage}
        </p>
      )}
      <p>{pendingDraftNote(pendingDraft)}</p>
      {confirming ? (
        <div
          role="group"
          aria-label={REPLACE_DRAFT}
          className="mt-3 rounded-md border border-amber-300 bg-white px-3 py-2"
        >
          <p className="text-[13px] text-gray-800">
            {replaceDraftWarning(pendingDraft)}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className={DANGER}
            >
              Choose the new file
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className={BTN}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setConfirming(true)}
            className={BTN}
          >
            {REPLACE_DRAFT}
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        aria-label="New plan file"
        data-testid="replace-draft-input"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          setConfirming(false);
          onReplace(f);
        }}
      />
    </div>
  );
};

export default PendingDraftNote;
